// THE APP'S BLE-CONTRACT CLIENT — delivery-roadmap phase 4.3.
//
// The app speaks `contracts/ble-v1.ts` — the message set frozen at phase 0.3,
// before the Simulated Dongle (stage 2.2) or the native BLE adapter (stage 5.3)
// existed. APP-INS-08 is the requirement: *the Installer App shall use the BLE
// message schemas and workflow defined by ICD-COM-04 for every compatible
// dongle*. One message set, whatever carries it.
//
// WHAT IS IMPORTED, AND WHAT IS NOT. Every TYPE below comes from the frozen
// contract by `import type`, so a divergence between this client and the
// agreement is a compile error rather than a review finding. Nothing is imported
// at RUNTIME: `import type` is fully erased under `verbatimModuleSyntax`, so the
// app bundle carries no cross-package dependency and the contract stays what its
// README says it is — a file nobody has to execute in order to obey. The few
// runtime constants this client needs are declared here and bound to the
// contract's own types (`const CONTRACT_VERSION: BleContractVersion = 1`), which
// is the same check by a different route.
//
// THE TRANSPORT IS PLUGGABLE, AND THAT IS THE WHOLE POINT.
//
//   loopback   — `mockDongle.ts`, an in-process dongle honouring the same
//                contract. Drives the mock adapter and the hermetic E2E tier.
//   http bridge— `createHttpBridgeTransport`, which POSTs opaque envelope frames
//                to the vertical slice's DEV server (`/__dev/ble`), which hands
//                them to the composed Simulated Dongle's BLE server. That server
//                does REAL Modbus against the Simulated Inverter. The bridge is
//                dev-only scaffolding standing in for the native BLE link of
//                phase 5.3 — see the factory's own note.
//   native BLE — phase 5.3. A third implementation of `BleTransport`; not one
//                line below changes.
//
// FRAMES, NOT MESSAGES, ARE THE UNIT. BLE hands us a small negotiated MTU, so a
// body larger than it goes out as an ordered run of chunks and comes back the
// same way. The chunk size is NOT a contract value (the link negotiates it); the
// contract fixes only the framing fields. Chunks carry BYTES, never string
// slices — a UTF-8 body cut at an arbitrary byte offset is not valid text — and
// the digest is checked over the REASSEMBLED payload before it is decoded, so a
// partially received body is never half-believed.
//
// THE DIGEST SUITE IS AN OUT-OF-BAND AGREEMENT, STATED HERE BECAUSE IT HAS TO BE
// SOMEWHERE. `payloadDigest` is `"<suite>:<encoded>"` and ADR-002 deliberately
// leaves the suite open, so the contract cannot tell two peers which one to use.
// This client emits and accepts `fnv1a64`, the suite the Simulated Dongle emits
// (`simulator/src/dongle/state.ts`). A production device substitutes an approved
// keyed construction behind the same prefix and this file changes in one place.
//
// NOTHING HERE CARRIES A SAFETY FUNCTION. No BMS limit, no protection setting,
// no activation command is reachable from this surface, and no register address
// appears anywhere in it — the app names `Australia A`, the dongle performs the
// allowlisted Modbus transaction, and the app cannot edit a register
// (APP-INS-01D, invariants 1 and 5 in `software/requirements/00-README.md`).

import type {
  BleAccountRef,
  BleChunkHeader,
  BleCommissioningOutcome,
  BleCommissioningRecordResultBody,
  BleCommissioningRunResultBody,
  BleCommissioningSummaryResultBody,
  BleContractVersion,
  BleDiscoveryAdvertisementBody,
  BleErrorCode,
  BleErrorResultBody,
  BleEvidenceKind,
  BleHomeownerFirstRecord,
  BleLocalPairingEvent,
  BlePairingCommitResultBody,
  BlePairingStatusResultBody,
  BleRegionProfileId,
  BleRegionSelectResultBody,
  BleRegionStatusResultBody,
  BleRole,
  BleSessionAuthenticateResultBody,
  BleSessionChallengeResultBody,
  BleSessionKind,
  BleTransportResumeResultBody,
  BleWifiStatusResultBody,
} from '../../../contracts/ble-v1'

// ---------------------------------------------------------------------------
// contract constants, bound to the contract's own types
// ---------------------------------------------------------------------------

/** The frozen revision this build speaks. The annotation is the check: the
 *  contract types `BleContractVersion` as the literal, so a bump there fails
 *  here (DNG-NFR-03). */
export const BLE_CONTRACT_VERSION: BleContractVersion = 1

/** This build's N±2 window, offered on every challenge and echoed on refusal. */
export const BLE_SUPPORTED_CONTRACT_VERSIONS: number[] = [1, 2, 3]

/** The three AS/NZS 4777.2 profiles offered at the Australian launch, spelled
 *  exactly as ICD-COM-14D states them (APP-INS-01A). The annotation binds the
 *  list to the contract's closed union — a fourth entry will not compile. */
export const BLE_REGION_PROFILE_IDS: readonly BleRegionProfileId[] = ['Australia A', 'Australia B', 'Australia C']

/** The `unassigned` sentinel a replacement or pre-assignment dongle reports
 *  rather than omitting or blanking an identifier (DNG-ID-05, ICD-API-15). */
export const BLE_UNASSIGNED = 'unassigned'

/** The provenance label the inverter serial carries at every boundary: it
 *  arrives over the UNAUTHENTICATED USB Modbus link and is an inventory/anomaly
 *  signal, never an identity claim (DNG-SEC-13, ICD-COM-14B). */
export const BLE_MODBUS_UNTRUSTED = 'modbus_untrusted'

/** Link MTU default. NOT a contract value — the link negotiates it; this is only
 *  what the in-process transports use when nobody says otherwise. */
export const DEFAULT_BLE_MTU_BYTES = 180

// ---------------------------------------------------------------------------
// the wire, as this build manipulates it
// ---------------------------------------------------------------------------

/** One complete logical message: the frozen envelope plus a decoded body. */
export interface BleWireMessage {
  v: BleContractVersion
  kind: string
  messageId: string
  sessionId: string | null
  seq: number
  inReplyTo?: string
  chunk: BleChunkHeader
  body: unknown
}

/** One frame on the link: the envelope plus THIS chunk's bytes. `payload` is a
 *  plain number array so a frame is JSON-serializable end to end — which is what
 *  lets the dev HTTP bridge shuttle it without knowing what it carries. */
export interface BleWireFrame {
  v: BleContractVersion
  kind: string
  messageId: string
  sessionId: string | null
  seq: number
  inReplyTo?: string
  chunk: BleChunkHeader
  payload: number[]
}

const encoder = new TextEncoder()
const decoder = new TextDecoder()

export const encodeBleBody = (body: unknown): number[] => [...encoder.encode(JSON.stringify(body ?? {}))]

export const decodeBleBody = (payload: ReadonlyArray<number>): unknown => JSON.parse(decoder.decode(Uint8Array.from(payload)))

const FNV_OFFSET = 0xcbf29ce484222325n
const FNV_PRIME = 0x100000001b3n
const MASK64 = 0xffffffffffffffffn

/** FNV-1a/64 over UTF-16 code units, suite-prefixed. See the header note on why
 *  the suite is an out-of-band agreement and not a contract value (ADR-002). */
export function bleDigest(text: string): string {
  let hash = FNV_OFFSET
  for (let i = 0; i < text.length; i++) {
    hash ^= BigInt(text.charCodeAt(i))
    hash = (hash * FNV_PRIME) & MASK64
  }
  return `fnv1a64:${hash.toString(16).padStart(16, '0')}`
}

/** The digest over a reassembled payload, exactly as the dongle computes it. */
export const blePayloadDigest = (payload: ReadonlyArray<number>): string => bleDigest(payload.join(','))

/**
 * Cut one logical message into link frames.
 *
 * Any chunk header the caller supplied is REPLACED: the header is transport
 * metadata computed by the sending transport, so a message replayed from a
 * captured fixture (whose header describes the MTU and digest suite of a capture
 * this link knows nothing about) is still replayable here.
 */
export function bleFramesFor(message: BleWireMessage, mtuBytes: number = DEFAULT_BLE_MTU_BYTES): BleWireFrame[] {
  const payload = encodeBleBody(message.body)
  const size = Math.max(1, Math.trunc(mtuBytes))
  const chunkCount = Math.max(1, Math.ceil(payload.length / size))
  const transferId = message.chunk?.transferId || `xfer-${message.messageId}`
  const payloadDigest = blePayloadDigest(payload)
  const frames: BleWireFrame[] = []
  for (let index = 0; index < chunkCount; index++) {
    const slice = payload.slice(index * size, (index + 1) * size)
    frames.push({
      v: message.v,
      kind: message.kind,
      messageId: message.messageId,
      sessionId: message.sessionId,
      seq: message.seq,
      ...(message.inReplyTo !== undefined ? { inReplyTo: message.inReplyTo } : {}),
      chunk: { transferId, chunkIndex: index, chunkCount, totalBytes: payload.length, chunkBytes: slice.length, payloadDigest },
      payload: slice,
    })
  }
  return frames
}

export type BleReassemblyOutcome =
  | { status: 'complete'; message: BleWireMessage }
  | { status: 'partial'; received: number; of: number }
  | { status: 'error'; reason: string }

export interface BleReassembler {
  accept(frame: BleWireFrame): BleReassemblyOutcome
  /** Highest CONTIGUOUS index held for a transfer, or -1 — the resume cursor. */
  receivedThrough(transferId: string): number
  /** Transfers still waiting for frames, newest last. */
  pending(): Array<{ transferId: string; receivedThrough: number }>
  drop(transferId: string): void
  readonly open: number
}

/**
 * Reassembly, and the four ways it refuses.
 *
 * A refusal changes nothing and is resumable rather than restarted from an
 * assumed offset — failing closed is the rule for an unrecognised or damaged
 * exchange (DNG-NFR-03, DNG-NFR-05).
 */
export function createBleReassembler(): BleReassembler {
  interface Open {
    header: BleWireFrame
    chunks: Array<number[] | undefined>
  }
  const open = new Map<string, Open>()

  return {
    accept: (frame) => {
      const header = frame.chunk
      if (!header || !Number.isInteger(header.chunkCount) || header.chunkCount < 1) return { status: 'error', reason: 'chunk header is missing or malformed' }
      if (!Number.isInteger(header.chunkIndex) || header.chunkIndex < 0 || header.chunkIndex >= header.chunkCount)
        return { status: 'error', reason: `chunk index ${header.chunkIndex} is outside 0…${header.chunkCount - 1}` }
      if (frame.payload.length !== header.chunkBytes) return { status: 'error', reason: 'chunkBytes disagrees with the payload length' }

      const existing = open.get(header.transferId)
      const state: Open = existing ?? { header: frame, chunks: new Array(header.chunkCount).fill(undefined) }
      if (!existing) open.set(header.transferId, state)
      // A transfer's identity is fixed at its first chunk. A later chunk that
      // disagrees about the message it belongs to is not a smaller message — it
      // is a different one. Abandon rather than splice.
      if (state.header.messageId !== frame.messageId || state.header.kind !== frame.kind || state.chunks.length !== header.chunkCount) {
        open.delete(header.transferId)
        return { status: 'error', reason: 'a later chunk disagrees with the transfer it claims to belong to' }
      }
      state.chunks[header.chunkIndex] = frame.payload

      const received = state.chunks.filter((chunk) => chunk !== undefined).length
      if (received < header.chunkCount) return { status: 'partial', received, of: header.chunkCount }

      const payload = state.chunks.flatMap((chunk) => chunk ?? [])
      open.delete(header.transferId)
      if (payload.length !== header.totalBytes) return { status: 'error', reason: 'reassembled length disagrees with totalBytes' }
      if (blePayloadDigest(payload) !== header.payloadDigest) return { status: 'error', reason: 'payload digest does not match the reassembled body' }
      let body: unknown
      try {
        body = decodeBleBody(payload)
      } catch {
        return { status: 'error', reason: 'reassembled payload is not valid JSON' }
      }
      return {
        status: 'complete',
        message: {
          v: state.header.v,
          kind: state.header.kind,
          messageId: state.header.messageId,
          sessionId: state.header.sessionId,
          seq: state.header.seq,
          ...(state.header.inReplyTo !== undefined ? { inReplyTo: state.header.inReplyTo } : {}),
          chunk: header,
          body,
        },
      }
    },

    receivedThrough: (transferId) => {
      const state = open.get(transferId)
      if (!state) return -1
      let through = -1
      for (let index = 0; index < state.chunks.length; index++) {
        if (state.chunks[index] === undefined) break
        through = index
      }
      return through
    },

    pending: () => {
      const rows: Array<{ transferId: string; receivedThrough: number }> = []
      for (const [transferId, state] of open) {
        let through = -1
        for (let index = 0; index < state.chunks.length; index++) {
          if (state.chunks[index] === undefined) break
          through = index
        }
        rows.push({ transferId, receivedThrough: through })
      }
      return rows
    },

    drop: (transferId) => {
      open.delete(transferId)
    },

    get open() {
      return open.size
    },
  }
}

// ---------------------------------------------------------------------------
// the transport port
// ---------------------------------------------------------------------------

/**
 * Everything a link has to be able to do. Three implementations exist or will:
 * the in-process mock dongle, the dev HTTP bridge, and (phase 5.3) native BLE.
 *
 * `exchange` is deliberately frames-in/frames-out and not request/response: a
 * dongle may answer one request with several messages (a resume answer followed
 * by the re-emitted tail) and may push an unsolicited event alongside a reply.
 */
export interface BleTransport {
  /** What the human is actually connected through. Rendered in the UI, because
   *  a screen that says "connected over Bluetooth" when it is not would be the
   *  same class of lie as a fabricated test result. */
  readonly label: string
  /** True when this transport reaches a real device path (dev bridge, native);
   *  false for the in-process scripted dongle. */
  readonly simulated: boolean
  /** The advertisement the device is broadcasting, as frames. */
  advertise(): Promise<BleWireFrame[]>
  /** Hand frames over; collect whatever came back. */
  exchange(frames: ReadonlyArray<BleWireFrame>): Promise<BleWireFrame[]>
  /**
   * The ONSITE CREDENTIAL this link holds for the device on the other end, or
   * `null` when it holds none.
   *
   * A real onsite session is opened with a key the app was issued OUT OF BAND —
   * bonded to the link, held by the platform, never derivable from anything
   * published (DNG-NFR-02, ADR-002). Which credential that is, and whether there
   * is one at all, is a property of the LINK rather than of the screen asking for
   * a session: the native adapter of phase 5.3 will read it off the bond, and the
   * dev bridge fetches it from the composition root that minted it. So the
   * transport answers it, `openSession` uses it when it is there, and a transport
   * that has none (the in-process mock dongle) simply does not implement it and
   * the caller's own prover is used exactly as before.
   */
  onsiteProofKey?(): Promise<string | null>
}

// ---------------------------------------------------------------------------
// the injected prover — the app half of the injected-verifier pattern
// ---------------------------------------------------------------------------

/** What a prover is given. Everything is opaque except the role, the SESSION
 *  KIND and the account reference, which a real construction may bind to. */
export interface BleProofInput {
  sessionId: string
  role: BleRole
  /** The kind the dongle will grant. It is BOUND INTO THE PROOF because it
   *  decides the scopes the session receives — a `ledger_only` service session
   *  is granted `record_read`, which reaches the owner's NMI and address — and a
   *  value that decides authority may not be an unattested request parameter.
   *  Derived, not echoed: see `bleGrantedSessionKind`. */
  sessionKind: BleSessionKind
  accountRef: string
  proofSuite: string
  challenge: string
  deviceNonce: string
  appNonce: string
}

export type BleProver = (input: BleProofInput) => string | Promise<string>

/**
 * The session kind the dongle WILL grant for a role and a request.
 *
 * The mirror of `sessionKindFor` in `simulator/src/dongle/ble/sessions.ts`: a
 * `service` role is a ledger-only role whatever it asked for (DNG-COM-09D). It
 * is derived on both sides rather than echoed on the challenge result, because
 * `BleSessionChallengeResultBody` is frozen and does not carry it — deriving the
 * same value from the same rule needs no field and no contract change.
 */
export function bleGrantedSessionKind(role: BleRole, requested: BleSessionKind): BleSessionKind {
  return role === 'service' ? 'ledger_only' : requested
}

/** THE CANONICAL BINDING — the mirror of `bleProofBinding` in the simulator.
 *  One function on each side, so what a proof covers cannot drift. */
export function bleProofBinding(input: BleProofInput): string {
  return [input.proofSuite, input.sessionId, input.challenge, input.deviceNonce, input.appNonce, input.accountRef, input.role, input.sessionKind].join('|')
}

/**
 * A DETERMINISTIC TEST PROVER. NOT A SECURITY CONTROL.
 *
 * The mirror of `testProofVerifier` in `simulator/src/dongle/ble/sessions.ts`:
 * it binds the proof to the session, the challenge, both nonces, the account,
 * the role, the session kind and the negotiated suite — everything a real
 * construction would bind — using a public checksum with NO KEY. It proves the
 * session PLUMBING (a proof for the wrong session, account, kind or challenge is
 * refused) and nothing about cryptography, which ADR-002 leaves to the product.
 *
 * It only opens a session against a dongle that has explicitly accepted the
 * keyless construction. Against a real device — or the composed vertical slice,
 * which wires a KEYED verifier — use `createKeyedProver` with the material the
 * app was issued out of band, or the native adapter's own prover.
 */
export const deterministicTestProver: BleProver = (input) => bleDigest(bleProofBinding(input))

/**
 * The prover for a dongle whose verifier is KEYED: the app was given the onsite
 * key out of band (the bonded key, or an installer credential the cloud issued
 * after authenticating the human) and proves possession of it. The primitive
 * stays the product's choice (ADR-002); the injection point is this function.
 */
export function createKeyedProver(secret: string): BleProver {
  return (input) => bleDigest(`${secret}|${bleProofBinding(input)}`)
}

// ---------------------------------------------------------------------------
// outcomes
// ---------------------------------------------------------------------------

/**
 * Every call answers exactly one of these. A caller branches on `code`, never on
 * prose — the classified failure vocabulary is the contract's, plus `offline`
 * for "the link itself did not answer", which is a fact about the transport and
 * not a refusal by the device.
 */
export type BleFailureCode = BleErrorCode | 'offline'

export type BleOutcome<T> =
  | { ok: true; body: T }
  | { ok: false; code: BleFailureCode; detail: string; error?: BleErrorResultBody }

export interface BleSessionView {
  sessionId: string
  role: BleRole
  sessionKind: BleSessionKind
  grantedScopes: string[]
  deviceIdentityState: BleSessionAuthenticateResultBody['deviceIdentityState']
}

export interface BleClientOptions {
  mtuBytes?: number
  /** Identifier source. Deterministic in tests, random in the app. */
  ids?: (prefix: string) => string
  /** How many resume rounds to attempt before giving up on a torn transfer. */
  maxResumeAttempts?: number
}

export interface BleClient {
  readonly transport: BleTransport
  readonly session: BleSessionView | null
  /** Discovery — no session exists yet, and the advertisement carries no account
   *  information of any kind (DNG-COM-06, SIM-DNG-01, APP-OWN-01). */
  discover(): Promise<BleOutcome<BleDiscoveryAdvertisementBody>>
  /** Challenge + proof, in one call. Scopes come back GRANTED by the dongle from
   *  the authenticated role — never asserted by the app (DNG-COM-01). */
  openSession(options: {
    role: BleRole
    accountRef: string
    requestedSessionKind?: BleSessionKind
    prove: BleProver
    offeredProofSuites?: string[]
    offeredContractVersion?: number
  }): Promise<BleOutcome<BleSessionAuthenticateResultBody>>
  pairingStatus(): Promise<BleOutcome<BlePairingStatusResultBody>>
  pairingCommit(input: { event: BleLocalPairingEvent; actor: BleAccountRef; homeownerFirst?: BleHomeownerFirstRecord }): Promise<BleOutcome<BlePairingCommitResultBody>>
  /** Device-executed evidence ONLY. An installer-performed item is not something
   *  the device can be asked to do (APP-INS-01/02/03). */
  commissioningRun(input: { testRunId: string; catalogueId: string; catalogueVersion: string; itemId: string }): Promise<BleOutcome<BleCommissioningRunResultBody>>
  commissioningRecord(input: {
    testRunId: string
    catalogueId: string
    catalogueVersion: string
    itemId: string
    evidenceKind: Exclude<BleEvidenceKind, 'device_executed'>
    outcome: BleCommissioningOutcome
    recordedAt: number
    evidenceRef?: string
  }): Promise<BleOutcome<BleCommissioningRecordResultBody>>
  commissioningSummary(testRunId?: string): Promise<BleOutcome<BleCommissioningSummaryResultBody>>
  regionSelect(input: { requestId: string; requestedProfileId: BleRegionProfileId }): Promise<BleOutcome<BleRegionSelectResultBody>>
  regionStatus(): Promise<BleOutcome<BleRegionStatusResultBody>>
  /** READ ONLY on this surface: the four fields DNG-COM-07F permits. There is no
   *  passphrase field anywhere in a response, by construction (APP-SEC-11). */
  wifiStatus(): Promise<BleOutcome<BleWifiStatusResultBody>>
}

export function createBleClient(transport: BleTransport, options: BleClientOptions = {}): BleClient {
  const mtuBytes = options.mtuBytes ?? DEFAULT_BLE_MTU_BYTES
  const maxResumeAttempts = options.maxResumeAttempts ?? 4
  let counter = 0
  const ids = options.ids ?? ((prefix: string) => `${prefix}-${String(++counter).padStart(6, '0')}`)

  const reassembler = createBleReassembler()
  const seqBySession = new Map<string, number>()
  let session: BleSessionView | null = null

  const nextSeq = (sessionId: string | null): number => {
    const key = sessionId ?? '@epoch'
    const next = (seqBySession.get(key) ?? 0) + 1
    seqBySession.set(key, next)
    return next
  }

  const compose = (kind: string, body: unknown, sessionId: string | null): BleWireMessage => ({
    v: BLE_CONTRACT_VERSION,
    kind,
    messageId: ids('app-msg'),
    sessionId,
    seq: nextSeq(sessionId),
    chunk: { transferId: '', chunkIndex: 0, chunkCount: 1, totalBytes: 0, chunkBytes: 0, payloadDigest: '' },
    body,
  })

  /** Feed frames through reassembly; return the messages that completed. A frame
   *  that fails reassembly is dropped here and recovered by resume, not by
   *  guessing at what the missing bytes were. */
  const absorb = (frames: ReadonlyArray<BleWireFrame>): BleWireMessage[] => {
    const messages: BleWireMessage[] = []
    for (const frame of frames) {
      const outcome = reassembler.accept(frame)
      if (outcome.status === 'complete') messages.push(outcome.message)
    }
    return messages
  }

  /**
   * Send one logical message and wait for the reply that names it.
   *
   * RESUME, NOT RESTART. If the reply does not complete — the link dropped
   * frames mid-transfer — the client asks the dongle to resume the transfer from
   * the highest contiguous index it actually holds, rather than re-issuing the
   * request (which would re-execute it) or assuming an offset. `complete: true`
   * on the answer means "known, and nothing left to send", so the client stops
   * asking instead of looping (the 2026-08-01 v1 amendment; a build that never
   * emits it is handled by the attempt budget).
   */
  const exchange = async (kind: string, body: unknown, sessionId: string | null): Promise<BleOutcome<unknown>> => {
    const request = compose(kind, body, sessionId)
    let received: BleWireMessage[]
    try {
      received = absorb(await transport.exchange(bleFramesFor(request, mtuBytes)))
    } catch (e) {
      return { ok: false, code: 'offline', detail: e instanceof Error ? e.message : String(e) }
    }

    let reply = received.find((message) => message.inReplyTo === request.messageId)
    for (let attempt = 0; reply === undefined && attempt < maxResumeAttempts; attempt++) {
      const torn = reassembler.pending()
      if (torn.length === 0) break
      const target = torn[torn.length - 1]
      const resume = compose('transport.resume.request', { transferId: target.transferId, receivedThroughIndex: target.receivedThrough }, sessionId)
      let more: BleWireMessage[]
      try {
        more = absorb(await transport.exchange(bleFramesFor(resume, mtuBytes)))
      } catch (e) {
        return { ok: false, code: 'offline', detail: e instanceof Error ? e.message : String(e) }
      }
      const answer = more.find((message) => message.kind === 'transport.resume.result')
      const answerBody = answer?.body as BleTransportResumeResultBody | undefined
      if (answerBody && (answerBody.outcome !== 'resumable' || answerBody.complete === true)) {
        // Not resumable, or nothing left to send: stop asking. Either way the
        // reply is lost and the caller is told so — nothing is invented.
        reassembler.drop(target.transferId)
        if (answerBody.outcome !== 'resumable') break
      }
      reply = more.find((message) => message.inReplyTo === request.messageId)
      if (reply === undefined && answerBody?.complete === true) break
    }

    if (reply === undefined) return { ok: false, code: 'transport', detail: `no complete reply to ${kind} arrived over the link` }
    if (reply.kind === 'error.result') {
      const error = reply.body as BleErrorResultBody
      return { ok: false, code: error.code, detail: error.detail, error }
    }
    return { ok: true, body: reply.body }
  }

  const call = async <T>(kind: string, body: unknown): Promise<BleOutcome<T>> => {
    const outcome = await exchange(kind, body, session?.sessionId ?? null)
    return outcome.ok ? { ok: true, body: outcome.body as T } : outcome
  }

  return {
    transport,
    get session() {
      return session
    },

    discover: async () => {
      let frames: BleWireFrame[]
      try {
        frames = await transport.advertise()
      } catch (e) {
        return { ok: false, code: 'offline', detail: e instanceof Error ? e.message : String(e) }
      }
      const advertisement = absorb(frames).find((message) => message.kind === 'discovery.advertisement')
      if (!advertisement) return { ok: false, code: 'transport', detail: 'no advertisement was received' }
      return { ok: true, body: advertisement.body as BleDiscoveryAdvertisementBody }
    },

    openSession: async ({ role, accountRef, requestedSessionKind = 'pairing_capable', prove, offeredProofSuites = ['suite-a'], offeredContractVersion = BLE_CONTRACT_VERSION }) => {
      const appNonce = ids('app-nonce')
      const challenged = await exchange(
        'session.challenge.request',
        { role, requestedSessionKind, accountRef, appNonce, offeredContractVersion, offeredProofSuites },
        null,
      )
      if (!challenged.ok) return challenged
      const challenge = challenged.body as BleSessionChallengeResultBody
      // THE LINK'S OWN CREDENTIAL WINS. When the transport holds an onsite key
      // (the bonded material a native adapter is given, or — over the dev bridge
      // — the key the composition root's secure element minted for the composed
      // dongle), the proof is computed under it. A device that verifies with a
      // key does not accept anything else, so a caller-supplied prover here would
      // simply fail authentication; the caller's prover stays the answer for a
      // link that holds no credential, which is the in-process mock dongle.
      const linkKey = transport.onsiteProofKey ? await transport.onsiteProofKey().catch(() => null) : null
      const proveWith: BleProver = linkKey === null ? prove : createKeyedProver(linkKey)
      const proof = await proveWith({
        sessionId: challenge.sessionId,
        role,
        // The kind the DEVICE will grant, derived by the same rule the device
        // uses — never the raw request, which is what makes it an attestation.
        sessionKind: bleGrantedSessionKind(role, requestedSessionKind),
        accountRef,
        proofSuite: challenge.proofSuite,
        challenge: challenge.challenge,
        deviceNonce: challenge.deviceNonce,
        appNonce,
      })
      const authenticated = await exchange('session.authenticate.request', { sessionId: challenge.sessionId, proof }, challenge.sessionId)
      if (!authenticated.ok) return authenticated
      const body = authenticated.body as BleSessionAuthenticateResultBody
      session = {
        sessionId: body.sessionId,
        role: body.role,
        sessionKind: body.sessionKind,
        grantedScopes: [...body.grantedScopes],
        deviceIdentityState: body.deviceIdentityState,
      }
      return { ok: true, body }
    },

    pairingStatus: () => call<BlePairingStatusResultBody>('pairing.status.request', {}),
    pairingCommit: (input) => call<BlePairingCommitResultBody>('pairing.commit.request', input),
    commissioningRun: (input) => call<BleCommissioningRunResultBody>('commissioning.run.request', { ...input, evidenceKind: 'device_executed' }),
    commissioningRecord: (input) => call<BleCommissioningRecordResultBody>('commissioning.record.request', input),
    commissioningSummary: (testRunId) => call<BleCommissioningSummaryResultBody>('commissioning.summary.request', testRunId === undefined ? {} : { testRunId }),
    regionSelect: (input) => call<BleRegionSelectResultBody>('region.select.request', input),
    regionStatus: () => call<BleRegionStatusResultBody>('region.status.request', {}),
    wifiStatus: () => call<BleWifiStatusResultBody>('wifi.status.request', {}),
  }
}

// ---------------------------------------------------------------------------
// the DEV HTTP bridge — scaffolding for phase 5.3, and nothing more
// ---------------------------------------------------------------------------

/**
 * A transport that POSTs opaque envelope frames to the vertical slice's DEV
 * server, which hands them to the composed Simulated Dongle's BLE server.
 *
 * WHY IT EXISTS. A browser cannot open a BLE link. Until the native adapter of
 * phase 5.3 exists, the only way to run the REAL app UI against the REAL dongle
 * — which does REAL Modbus against the Simulated Inverter — is to tunnel the
 * frames. So that is all this does: it shuttles frames. It parses nothing,
 * understands nothing, and adds nothing to the envelope.
 *
 * WHY IT IS NOT A CLOUD ROUTE, AND MUST NOT BECOME ONE. The device accepts no
 * inbound WAN connection and BLE is a LOCAL listener (invariant 3, DNG-COM-01,
 * ICD-COM-07C). A product cloud route that reached a dongle's BLE surface would
 * contradict both. The bridge therefore lives only on `vertical-slice/src/devServer.ts`,
 * under the `/__dev/` prefix that server already reserves for demo hand-holds,
 * and the deployed cloud (`cloud/`, `infra/`) has no such route and never gains
 * one. A build pointed at a real cloud gets 404 here, and the client reports the
 * link as unavailable rather than pretending.
 */
export interface HttpBridgeOptions {
  /** Base of the dev server, e.g. `http://localhost:8787`. */
  baseUrl: string
  /** Injected for tests; defaults to the global. */
  fetchImpl?: typeof fetch
  /** Path prefix on the dev server. Dev-only by construction. */
  prefix?: string
}

export function createHttpBridgeTransport(options: HttpBridgeOptions): BleTransport {
  const prefix = options.prefix ?? '/__dev/ble'
  const base = options.baseUrl.replace(/\/+$/, '')
  const doFetch = options.fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args))

  const post = async (path: string, body: unknown): Promise<BleWireFrame[]> => {
    const response = await doFetch(`${base}${prefix}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (response.status === 404)
      throw new Error('No BLE bridge on this backend. The bridge is dev-server-only scaffolding; a real deployment needs the native BLE adapter (phase 5.3).')
    if (!response.ok) throw new Error(`BLE bridge answered ${response.status}`)
    const payload = (await response.json()) as { frames?: BleWireFrame[] }
    return payload.frames ?? []
  }

  /**
   * The onsite credential for the composed dongle behind this bridge.
   *
   * A NATIVE BLE CLIENT ALREADY HAS ONE — issued out of band once the cloud has
   * authenticated the human, and bonded to the link. A browser has neither radio
   * nor bond, so the dev server hands over the key the composed device's secure
   * element minted (`vertical-slice/src/composition.ts` → `attachBle`, exposed at
   * `/__dev/ble/proof-key`). It is the same dev-only stand-in the frame tunnel
   * is, on the same reserved prefix, in the same file no build produces.
   *
   * IT WEAKENS NOTHING: the dongle still requires a keyed proof bound to the
   * session, the challenge, both nonces, the account, the role and the session
   * kind, and still refuses the public keyless construction. Only the holder of
   * the key changes.
   *
   * Asked of anything that is NOT that dev server — a real cloud, a bridge from
   * before this route existed — the answer is 404 and the honest reply is `null`:
   * this link holds no credential, so `openSession` uses the prover it was given
   * rather than inventing authority it does not have. Cached because the key is a
   * property of the composed device, not of the request.
   */
  let cachedKey: string | null | undefined
  const onsiteProofKey = async (): Promise<string | null> => {
    if (cachedKey !== undefined) return cachedKey
    const response = await doFetch(`${base}${prefix}/proof-key`, { method: 'GET' })
    if (!response.ok) return (cachedKey = null)
    const payload = (await response.json()) as { proofKey?: unknown }
    return (cachedKey = typeof payload.proofKey === 'string' && payload.proofKey.length > 0 ? payload.proofKey : null)
  }

  return {
    label: 'dev BLE bridge → Simulated Dongle (real Modbus)',
    simulated: false,
    advertise: () => post('/advertise', {}),
    exchange: (frames) => post('/frames', { frames }),
    onsiteProofKey,
  }
}
